/*
 * Formulario para el registro de órdenes de traspaso.
 *
 * Contiene las funciones de guardado y actualización de órdenes de traspaso. Contempla
 * la información general de la orden de traspaso, productos y circuitos.
 *
 * Created on Mon Mar 13 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Form validation observer -->
    <validation-observer ref="transferForm">

      <!-- #region::Form -->
      <b-form @submit="$event.preventDefault()">

        <!-- #region::Card for main options -->
        <div class="mb-2">
          <MainOptions
            :title="trasnferId ? 'Actualizar traspaso' : 'Nuevo traspaso'"
            :save-button-text="trasnferId ? 'Guardar cambios' : 'Guardar traspaso'"
            model-home-route="transfers-list"
            :callback="saveTransfer"
          />
        </div>
        <!-- #endregion::Card for main options -->

        <!-- #region::Skeleton zone -->
        <template v-if="loadingDataForm">
          <TransferSaveFormSkeleton />
        </template>
        <!-- #endregion::Skeleton zone -->

        <template v-else>

          <!-- #region::Main information for transfer -->
          <TransferInformation />
          <!-- #endregion::Main information for transfer -->

          <b-col md="12">
            <b-tabs>

              <!-- #region::Transfer products list -->
              <b-tab title="Productos">
                <TransferProducts />
              </b-tab>
              <!-- #endregion::Transfer products list -->

              <!-- #region::Transfer circuits list -->
              <b-tab title="Circuitos">
                <TransferCircuits />
              </b-tab>
              <!-- #endregion::Transfer circuits list -->

            </b-tabs>
          </b-col>
        </template>

      </b-form>
      <!-- #endregion::Form -->

    </validation-observer>
    <!-- #endregion::Form validation observer -->

  </div>
</template>

<script>
// #region Imports
import { mapGetters, mapActions } from 'vuex'
import { ValidationObserver, localize } from 'vee-validate'

import {
  BForm, BCol, BTabs, BTab,
} from 'bootstrap-vue'

import MainOptions from '@/components/forms/MainOptions.vue'
import TransferProducts from '@/modules/production/transfers/components/form/TransferProducts.vue'
import TransferCircuits from '@/modules/production/transfers/components/form/TransferCircuits.vue'
import TransferInformation from '@/modules/production/transfers/components/form/TransferInformation.vue'
import TransferSaveFormSkeleton from '@/modules/production/transfers/components/TransferSaveFormSkeleton.vue'

import getError from '@/helpers/ErrorsHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// #endregion

export default {
  components: {
    BCol,
    BTab,
    BTabs,
    BForm,
    MainOptions,
    TransferCircuits,
    TransferProducts,
    ValidationObserver,
    TransferInformation,
    TransferSaveFormSkeleton,
  },
  props: {
    trasnferId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loadingDataForm: false,
    }
  },
  computed: {
    ...mapGetters({
      /**
       * Información general de la orden de traspaso
       */
      getSelectedOrigin: 'transfers/getSelectedOrigin',
      getSelectedDestination: 'transfers/getSelectedDestination',
      getSelectedRequiredDate: 'transfers/getSelectedRequiredDate',

      /**
       * Productos y circuitos de la orden de traspaso
       */
      getTransferProducts: 'transfers/getTransferProducts',
      getTransferCircuits: 'transfers/getTransferCircuits',

      /**
       * Término de ubicación de búsqueda del store de productos
       */
      getLocationTerm: 'products/getLocationTerm',
    }),
    /**
     * Ubicación de origen seleccionada
     */
    selectedOrigin: {
      get() { return this.getSelectedOrigin },
    },
    /**
     * Ubicación de destino seleccionada
     */
    selectedDestination: {
      get() { return this.getSelectedDestination },
    },
    /**
     * Fecha requerida seleccionada
     */
    selectedRequiredDate: {
      get() { return this.getSelectedRequiredDate },
    },
    /**
     * Productos de la orden de traspaso
     */
    transferProducts: {
      get() { return this.getTransferProducts },
    },
    /**
     * Circuitos de la orden de traspaso
     */
    transferCircuits: {
      get() { return this.getTransferCircuits },
    },
    /**
     * Término de ubicación de búsqueda del store de productos
     */
    locationTerm: {
      get() { return this.getLocationTerm },
      set(value) { this.setLocationTerm(value) },
    },
  },
  created() {
    localize('es')
  },
  /**
  * Llamado antes de cambiar de componente
  *
  * @summary Se encarga de limpiar toda la información en el state
  * management relacionada con el detalle de la orden de traspaso
  */
  beforeDestroy() {
    this.locationTerm = ''
    this.clearFormData()
  },
  methods: {
    ...mapActions({
      clearFormData: 'transfers/clearFormData',
      createTransfer: 'transfers/createTransfer',
      setLocationTerm: 'products/setLocationTerm',
    }),

    /**
    * Almacena una orden de traspaso
    *
    * @summary Crea la estructura de información necesaria a partir de productos
    * y circuitos. Agrega la información de la orden de traspaso al formData.
    * @exception Lanzada si existe un error en la petición al servidor
    */
    async saveTransfer() {
      const success = await this.$refs.transferForm.validate()

      if (success) {
        try {
          this.$swal({
            title: 'Espere por favor',
            allowOutsideClick: false,
          })

          this.$swal.showLoading()

          const formData = new FormData()

          // * Si existen productos en el formalario, serán agregados al formData.
          this.transferProducts.forEach(transferProduct => {
            formData.append('circuits_products[]', this.buildProductData(transferProduct))
          })

          // * Si existen circuitos en el formulario, serán agregados al formData.
          this.transferCircuits.forEach(transferCircuit => {
            transferCircuit.products.forEach(product => {
              formData.append('circuits_products[]', this.buildProductData(product, transferCircuit.IdCircuit))
            })
          })

          formData.append('locationOrigin', this.selectedOrigin.text)
          formData.append('dateRequired', this.selectedRequiredDate)
          formData.append('locationDestiny', this.selectedDestination.text)

          // eslint-disable-next-line no-restricted-syntax
          for (const pair of formData.entries()) {
            console.log(`${pair[0]}, ${pair[1]}`)
          }

          await this.createTransfer(formData)
          this.showSwalFire('¡Los datos de la orden de traspaso se han guardado correctamente!')
        } catch (error) {
          this.$swal.close()
          this.showToast('Error de validación', getError(error), 'danger')
        }
      } else {
        this.showToast(
          'Error de validación',
          'Se encontraron uno o más errores en la validación del formulario de la orden de traspaso.',
          'danger',
        )
      }
    },

    /**
    * Construye un objeto de tipo producto con sus piezas en formato JSON
    *
    * @param product - Información del producto
    * @param {number, string} idCircuit - Identificador del circuito. Especificado si el producto pertenece a un circuito
    * @return Producto con piezas en format JSON
    */
    buildProductData(product, idCircuit = '') {
      const pieces = []

      product.pieces.forEach(piece => {
        pieces.push({ idPiece: piece.IdPiece })
      })

      const productData = JSON.stringify({
        idCircuit,
        idProduct: product.IdProduct,
        qty: product.pieces.length,
        pieces,
      })

      return productData
    },

    /**
    * Modal de confirmación
    *
    * @summary Muestra un mensaje existoso al crear la orden de traspaso. Redirige a la lista de traspasos
    * @param {string} message - Mensaje para mostrar en el modal
    */
    showSwalFire(message) {
      this.$swal.fire({
        title: 'Guardado',
        text: message,
        icon: 'success',
        showCancelButton: false,
        confirmButtonText: 'Continuar',
        allowEscapeKey: false,
        allowOutsideClick: false,
      }).then(result => {
        if (result.isConfirmed) {
          this.savedSuccess = true
          this.$router.push({ name: 'transfers-list' })
        }
      })
    },

    /**
    * Mensaje de alerta
    *
    * @summary Muestra un mensaje de tipo toast
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
